import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Bell, Mail } from 'lucide-react';

/**
 * Shown ONCE per user the first time they visit the account settings
 * page. Asks for two opt-ins:
 *   1. Push notifications (browser/native)
 *   2. Email newsletter subscription
 * The "asked" state is persisted in `user.preferences.notif_prompt_seen`
 * so we never nag a user who has already answered.
 */
const FirstLoginNotificationsModal = () => {
  const { user, refreshUser } = useAuth();
  const { isSpanish } = useLanguage();
  const [open, setOpen] = useState(false);
  const [wantsPush, setWantsPush] = useState(true);
  const [wantsEmail, setWantsEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const alreadyAsked = user?.preferences?.notif_prompt_seen;
    if (!alreadyAsked) {
      // Delay so the page paints first — feels less abrupt
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  const dismiss = async (saved = false) => {
    setOpen(false);
    try {
      await apiFetch('/api/auth/preferences', {
        method: 'PUT',
        body: {
          notif_prompt_seen: true,
          wants_push: saved ? wantsPush : false,
          wants_email: saved ? wantsEmail : false,
        },
      });
      if (refreshUser) refreshUser();
    } catch (e) {
      // Non-fatal — modal closes regardless
    }
  };

  const handleSave = async () => {
    setSaving(true);
    // Browser notification permission request (only if user opted in
    // AND browser supports it; the actual native subscription happens
    // wherever the rest of the push code lives).
    if (wantsPush && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch {
        // ignore — user dismissed
      }
    }
    await dismiss(true);
    toast.success(
      isSpanish
        ? '¡Listo! Actualizamos tus preferencias.'
        : 'Got it — your preferences are saved.'
    );
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="first-login-notifications-modal">
        <DialogHeader>
          <DialogTitle>
            {isSpanish ? '¡Bienvenido/a! Mantente conectado' : 'Welcome! Stay in the loop'}
          </DialogTitle>
          <DialogDescription>
            {isSpanish
              ? 'Elige cómo te gustaría recibir actualizaciones. Puedes cambiar esto en cualquier momento.'
              : 'Pick how you\'d like to hear from us. You can change this any time in settings.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/40 cursor-pointer transition-colors">
            <Checkbox
              checked={wantsPush}
              onCheckedChange={(v) => setWantsPush(!!v)}
              data-testid="optin-push"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium text-foreground">
                <Bell className="h-4 w-4 text-[#00A896]" />
                {isSpanish ? 'Notificaciones push' : 'Push notifications'}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isSpanish
                  ? 'Recordatorios de racha, nuevas frases y notas amigables del Detective Dino.'
                  : 'Streak reminders, new phrases, and friendly nudges from Detective Dino.'}
              </p>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/40 cursor-pointer transition-colors">
            <Checkbox
              checked={wantsEmail}
              onCheckedChange={(v) => setWantsEmail(!!v)}
              data-testid="optin-email"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium text-foreground">
                <Mail className="h-4 w-4 text-[#00A896]" />
                {isSpanish ? 'Boletín por email' : 'Email newsletter'}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isSpanish
                  ? 'Resumen mensual de nuevas frases, blogs y consejos. Sin spam.'
                  : 'Monthly digest of new phrases, blogs, and tips. No spam, ever.'}
              </p>
            </div>
          </label>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => dismiss(false)} data-testid="optin-skip">
            {isSpanish ? 'Por ahora no' : 'Not now'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-semibold"
            data-testid="optin-save"
          >
            {saving ? (isSpanish ? 'Guardando…' : 'Saving…') : (isSpanish ? 'Guardar preferencias' : 'Save preferences')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FirstLoginNotificationsModal;
